import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BACKEND_URL, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, ArrowRight } from "lucide-react";

/**
 * AdminOtpIntake — Phase 5.4.
 * Admin / back-office staff drag an existing signed OTP into the system.
 * Creates the matter, stores the OTP as a classified upload (document_type='otp'),
 * auto-completes the offer-received task, and returns the portal link to share.
 */
export default function AdminOtpIntake({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    property_value: "",
    property_type: "freehold",
    has_bond: "true",
    buyer_type: "individual",
    matter_type: "conveyancing",
  });
  const [file, setFile] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Attach the signed OTP file first.");
      return;
    }
    setBusy(true);
    try {
      const qs = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        property_value: form.property_value,
        property_type: form.property_type,
        has_bond: form.has_bond,
        buyer_type: form.buyer_type,
        matter_type: form.matter_type,
      }).toString();
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("eh_token");
      const res = await fetch(`${BACKEND_URL}/api/admin/otp/intake?${qs}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw { response: { data } };
      toast.success("Matter opened and OTP filed.");
      setOpen(false);
      onCreated?.(data);
      navigate(`/admin/matters/${data.matter_id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="bg-zinc-900 hover:bg-zinc-800 text-white px-4 py-2 text-sm inline-flex items-center gap-2"
        data-testid="admin-otp-intake-btn"
      >
        <UploadCloud className="h-4 w-4" /> Upload OTP → open matter
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-xl" data-testid="admin-otp-dialog">
          <DialogHeader>
            <div className="text-overline text-zinc-500">OTP intake</div>
            <DialogTitle className="font-serif text-3xl">Open a matter from a signed OTP</DialogTitle>
            <DialogDescription>
              Drag the signed Offer to Purchase here. We&apos;ll create the matter, file the OTP and send
              the client their secure portal link.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Full name" v={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="otp-name" required />
              <Field label="Phone"     v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="otp-phone" required />
            </div>
            <Field label="Email" type="email" v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="otp-email" required />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Property value (ZAR)" v={form.property_value}
                onChange={(v) => setForm({ ...form, property_value: v.replace(/[^0-9.]/g, "") })}
                testid="otp-value" required />
              <div>
                <Label>Matter type</Label>
                <Select value={form.matter_type} onValueChange={(v) => setForm({ ...form, matter_type: v })}>
                  <SelectTrigger className="rounded-none mt-1" data-testid="otp-matter-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="conveyancing">Conveyancing</SelectItem>
                    <SelectItem value="antenuptial">Antenuptial</SelectItem>
                    <SelectItem value="trust">Trust</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Signed OTP file</Label>
              <input
                type="file"
                accept="application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm border border-zinc-200 p-2"
                data-testid="otp-file"
                required
              />
              {file && <p className="text-xs text-zinc-500 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-none bg-[#0055FF] hover:bg-[#003ECC]" data-testid="otp-submit">
              {busy ? "Filing OTP…" : "Open matter from OTP"} <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              The OTP is filed with <code>document_type=&quot;otp&quot;</code>, <code>extraction_status=&quot;pending&quot;</code>. AI-based field extraction is planned for Phase 6 — until then admin populates intake data manually.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, v, onChange, type = "text", testid, required }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type={type}
        required={required}
        value={v}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-none mt-1"
        data-testid={testid}
      />
    </div>
  );
}
